import type { User } from "firebase/auth";

export interface IntelligenceShipGate {
  productEnabled: boolean;
  previewOrganizationIds: string[];
  source?: "firestore" | "env" | string;
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

const parseError = async (response: Response): Promise<string> => {
  const data = (await response.json().catch(() => null)) as unknown;
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      return String((detail as { message: unknown }).message);
    }
  }
  return `Backend error (${response.status}).`;
};

export const fetchIntelligenceShipGate = async (
  user: User
): Promise<IntelligenceShipGate> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/ship-gate`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json()) as IntelligenceShipGate;
  return {
    productEnabled: Boolean(data.productEnabled),
    previewOrganizationIds: Array.isArray(data.previewOrganizationIds)
      ? data.previewOrganizationIds.map(String)
      : [],
    source: data.source,
  };
};

export const updateIntelligenceShipGate = async (
  user: User,
  payload: {
    productEnabled: boolean;
    previewOrganizationIds: string[];
  }
): Promise<IntelligenceShipGate> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/ship-gate`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json()) as IntelligenceShipGate;
  return {
    productEnabled: Boolean(data.productEnabled),
    previewOrganizationIds: Array.isArray(data.previewOrganizationIds)
      ? data.previewOrganizationIds.map(String)
      : [],
    source: data.source,
  };
};
